'use client';

import { ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocale } from '@instamenu/context-i18n';
import { useDisclosure } from '@instamenu/react-hooks';
import * as styles from './reasoning.css';

type ReasoningContextType = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const ReasoningContext = createContext<ReasoningContextType | undefined>(undefined);

function useReasoningContext() {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error('useReasoningContext must be used within a ReasoningProvider');
  }
  return context;
}

export type ReasoningProps = PropsWithChildren<{
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isStreaming?: boolean;
}>;

export function Reasoning(props: ReasoningProps) {
  const { children, className, open, onOpenChange, isStreaming } = props;
  const [internalOpen, { open: openInternal, close: closeInternal }] = useDisclosure();
  const [wasAutoOpened, setWasAutoOpened] = useState(false);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        if (newOpen) openInternal();
        else closeInternal();
      }

      onOpenChange?.(newOpen);
    },
    [closeInternal, isControlled, onOpenChange, openInternal],
  );

  useEffect(() => {
    if (isStreaming && !wasAutoOpened) {
      if (!isControlled) openInternal();
      setWasAutoOpened(true);
    }

    if (!isStreaming && wasAutoOpened) {
      if (!isControlled) closeInternal();
      setWasAutoOpened(false);
    }
  }, [closeInternal, isControlled, isStreaming, openInternal, wasAutoOpened]);

  return (
    <ReasoningContext.Provider value={{ isOpen, onOpenChange: handleOpenChange }}>
      <div className={clsx(styles.root, className)}>{children}</div>
    </ReasoningContext.Provider>
  );
}

export type ReasoningTriggerProps<T extends HTMLButtonElement = HTMLButtonElement> =
  PropsWithChildren<
    {
      className?: string;
    } & ButtonHTMLAttributes<T>
  >;

export function ReasoningTrigger<T extends HTMLButtonElement = HTMLButtonElement>(
  props: ReasoningTriggerProps<T>,
) {
  const { children, className, type, ...rest } = props;
  const { isOpen, onOpenChange } = useReasoningContext();
  const { i18n } = useLocale();

  const handleClick = () => {
    onOpenChange(!isOpen);
  };

  return (
    <button
      className={clsx(styles.trigger, className)}
      onClick={handleClick}
      {...rest}
      type={type ?? 'button'}
    >
      <span className={styles.triggerLabel}>{children ?? i18n.t('aiAssistant.reasoning')}</span>
      <div className={styles.chevron[isOpen ? 'open' : 'closed']}>
        <ChevronDown size={16} />
      </div>
    </button>
  );
}

export type ReasoningContentProps = PropsWithChildren<
  {
    className?: string;
    contentClassName?: string;
    markdown?: boolean;
  } & HTMLAttributes<HTMLDivElement>
>;

export function ReasoningContent(props: ReasoningContentProps) {
  const { children, className, contentClassName, markdown = false, ...rest } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const { isOpen } = useReasoningContext();
  const [maxHeight, setMaxHeight] = useState('0px');

  useEffect(() => {
    if (!contentRef.current || !innerRef.current) return;

    const updateHeight = () => {
      const height = innerRef.current?.scrollHeight ?? 0;
      setMaxHeight(isOpen ? `${height}px` : '0px');
    };

    const observer = new ResizeObserver(updateHeight);
    observer.observe(innerRef.current);
    updateHeight();

    return () => observer.disconnect();
  }, [isOpen]);

  const content =
    markdown && typeof children === 'string' ? (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
    ) : (
      children
    );

  return (
    <div
      ref={contentRef}
      className={clsx(styles.content, className)}
      style={{ maxHeight }}
      {...rest}
    >
      <div ref={innerRef} className={clsx(styles.contentInner, contentClassName)}>
        {content}
      </div>
    </div>
  );
}
